import { useTranslator } from '@translator-app/react';
import Fuse from 'fuse.js';
import { request } from 'graphql-request';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';

import { Image } from '../components/atoms/image';
import { ErrorFallback } from '../components/organisms/error-boundary';
import { SiteFooter } from '../components/organisms/site-footer';
import { SiteNav } from '../components/organisms/site-nav';
import { FallbackPageTemplate } from '../components/templates/fallback-page-template';
import { PageTemplate } from '../components/templates/page-template';
import { GRAPHQL_ENDPOINT, IS_PRODUCTION } from '../constants';
import { WoytiShopPaginatedResultObjectType } from '../generated/graphql';
import ArrowUpRightIcon from '../icons/arrow-up-right.svg';
import MapPinIcon from '../icons/map-pin.svg';
import SearchIcon from '../icons/search.svg';
import { MinimalServerError } from '../types';
import { ShopFragment } from '../utils/api';
import { Sentry, captureException } from './_app';

const PAGE_SIZE = 100;

const ShopsQuery = `
${ShopFragment}

query ListShops($pagination: PaginationInputObjectType, $testmode: Boolean!) {
  getWoytiShops(pagination: $pagination, testmode: $testmode) {
    count
    total
    limit
    skip
    docs {
      ...shopFields
    }
  }
}
`;

const getShopLink = (shop, locale) => {
  return `/${locale}/${shop.organisation ? shop.organisation.pathname : 'null'}/${shop._id}`;
};

interface PageProps {
  shops: WoytiShopPaginatedResultObjectType;
  skip: number;
  error?: MinimalServerError;
}

const PageImpl: React.FC<PageProps> = (props) => {
  const { shops } = props;
  const { translate } = useTranslator();
  const router = useRouter();
  const [searchValue, setSearchValue] = useState('');

  const sortedShops = useMemo(() => {
    return [...shops.docs].sort((a, b) => {
      const diff = b.woytiReservationsCount - a.woytiReservationsCount;
      if (diff !== 0) {
        return diff;
      }
      return a.name.localeCompare(b.name);
    });
  }, [shops.docs]);

  const filteredShops = useMemo(() => {
    if (!searchValue.trim()) {
      return sortedShops;
    }

    const fuse = new Fuse(sortedShops, {
      isCaseSensitive: false,
      keys: ['name', 'address.city', 'address.zipcode'],
      threshold: 0.4,
    });

    return fuse.search(searchValue).map((result) => result.item);
  }, [sortedShops, searchValue]);

  return (
    <div className="min-h-screen bg-cream text-foreground">
      <section className="bg-white pb-8 pt-12 md:pb-10 md:pt-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-3 text-xs font-medium uppercase tracking-widest text-primary-dark">
            {translate('homeScreen.shopLabel')}
          </div>
          <h1 className="font-display-bold text-4xl tracking-tight text-dark md:text-5xl">
            {translate('homeScreen.title')}{' '}
            <span className="text-primary">{translate('homeScreen.titleHighlight')}</span>
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg whitespace-pre-line">
            {translate('homeScreen.description')}
          </p>
        </div>
      </section>

      <div className="sticky top-16 z-30 border-y border-border bg-white/90 py-4 backdrop-filter backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-6">
          <div className="relative min-w-0" style={{ minWidth: '200px' }}>
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <SearchIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <label htmlFor="shop-search" className="sr-only">
              {translate('homeScreen.searchLabel')}
            </label>
            <input
              id="shop-search"
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={translate('homeScreen.searchPlaceholder')}
              className="w-full rounded-full border border-border bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-primary"
            />
          </div>
        </div>
      </div>

      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="font-display-bold text-xl text-dark">
              {translate('homeScreen.merchantCount', { count: filteredShops.length })}
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filteredShops.length === 0 ? (
              <div className="col-span-full py-16 text-center text-muted-foreground">
                {translate('homeScreen.noResults')}
              </div>
            ) : (
              filteredShops.map((shop) => {
                const activeCount = shop.woytiReservationsCount || 0;

                return (
                  <a
                    key={shop._id}
                    href={getShopLink(shop, router.locale)}
                    className="group block h-full rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-1 hover:border-primary hover:shadow-xl"
                  >
                    <div className="flex items-start justify-between gap-3">
                      {shop.organisation?.logoUrl ? (
                        <Image
                          url={shop.organisation.logoUrl}
                          alt={shop.name}
                          width={200}
                          height={96}
                          sizes="200px"
                          className="h-24 w-auto shrink-0 object-contain"
                        />
                      ) : (
                        <div className="flex h-24 w-24 shrink-0 items-center justify-center bg-primary-light font-display-bold text-2xl text-primary-dark">
                          {shop.name
                            .trim()
                            .split(/\s+/)
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((word) => word[0])
                            .join('')
                            .toUpperCase()}
                        </div>
                      )}

                      {activeCount > 0 && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                          {translate('homeScreen.activeOffers', { count: activeCount })}
                        </span>
                      )}
                    </div>

                    <h3 className="mt-5 font-display-bold text-lg leading-tight text-dark group-hover:text-primary">
                      {shop.name}
                    </h3>

                    <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
                      {shop.address?.zipcode} {shop.address?.city}
                    </p>

                    <div className="mt-5 flex items-center gap-1.5 text-sm font-medium text-primary">
                      {translate('homeScreen.viewMerchant')}
                      <ArrowUpRightIcon className="h-4 w-4 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </a>
                );
              })
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default function Index(props: PageProps) {
  if (props.error) {
    return (
      <FallbackPageTemplate>
        <ErrorFallback />
      </FallbackPageTemplate>
    );
  }

  return (
    <PageTemplate>
      <SiteNav showCart={false} shopLinkActive />

      <PageImpl {...props} />

      <SiteFooter />
    </PageTemplate>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  try {
    let { skip = '0' } = ctx.query;
    let parsedSkip = +(Array.isArray(skip) ? skip[0] : skip) || 0;
    let result = await request<{
      getWoytiShops: WoytiShopPaginatedResultObjectType;
    }>(GRAPHQL_ENDPOINT, ShopsQuery, {
      pagination: {
        skip: parsedSkip,
        limit: parsedSkip + PAGE_SIZE,
      },
      testmode: !IS_PRODUCTION,
    });

    return {
      props: {
        shops: result.getWoytiShops,
      },
    };
  } catch (err) {
    const eventId = captureException(err);
    await Sentry.flush();

    return {
      props: {
        error: {
          message: err.message,
          eventId,
        },
      },
    };
  }
}
